/**
 * Minimal route resolution for the status pages.
 *
 * No router library on purpose. The app has four runtime dependencies and
 * `npm audit --omit=dev --audit-level=high` is a blocking CI job; adding a
 * router for three static routes buys nothing and widens that surface.
 *
 * Three addressing forms, all resolving to the same pages:
 *
 *   On the status host (status.cerinahealth.com), where the status page IS the site:
 *     /                        /incidents            /incidents/INC-1A2B
 *
 *   On the app host (wire-staging.cerinahealth.com), where it is a section:
 *     /status                  /status/incidents     /status/incidents/INC-1A2B
 *
 *   Hash form, for when CDN deep-link config is missing:
 *     #/status                 #/status/incidents    #/status/incidents/INC-1A2B
 *
 * The path forms need the SPA fallback on the CDN (403/404 → /index.html with a
 * 200). The hash form needs no CDN configuration at all, so it is the safe link
 * to share if deep links 404.
 */

export type Route =
  | { name: "app" }
  | { name: "status" }
  | { name: "incidents" }
  | { name: "incident"; reference: string };

/**
 * True on a hostname dedicated to the status page.
 *
 * Deliberately a prefix check rather than an exact match, so it holds for
 * status.cerinahealth.com, a staging equivalent, and localhost testing via
 * `status.localhost` — without hard-coding an environment list that would
 * silently fail in the next one.
 */
export function isStatusHost(location: Location = window.location): boolean {
  return location.hostname.startsWith("status.");
}

export function resolveRoute(location: Location = window.location): Route {
  const hash = location.hash.replace(/^#/, "");
  const usingHash = hash.startsWith("/");
  // Production serves the app under /llr/ (preview at /), so the deployed
  // path is /llr/status — strip the base before matching or every status
  // route silently falls through to the chat. Fails safe either way: an
  // unmatched path is the app, which is what it already was.
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const rawPath =
    base && location.pathname.startsWith(base)
      ? location.pathname.slice(base.length) || "/"
      : location.pathname;
  const source = (usingHash ? hash : rawPath).replace(/\/+$/, "");

  // On the status host the /status prefix is implied, so accept it either way:
  // status.cerinahealth.com/incidents and .../status/incidents both work, and
  // an old link survives the move.
  const stripped =
    isStatusHost(location) && !source.toLowerCase().startsWith("/status")
      ? `/status${source}`
      : source;

  const path = stripped.toLowerCase();
  if (path === "/status" || (isStatusHost(location) && path === "")) {
    return { name: "status" };
  }
  if (path === "/status/incidents") return { name: "incidents" };

  // Matched against the original casing — references are uppercase and must
  // survive the lowercasing above, or every deep link 404s.
  const match = stripped.match(/^\/status\/incidents\/(.+)$/i);
  if (match) return { name: "incident", reference: decodeURIComponent(match[1]) };

  return { name: "app" };
}

/**
 * Build a link to one of these pages, in whichever form the page is already
 * using — so navigation never bounces a reader between hosts or addressing
 * styles mid-incident.
 */
export function hrefFor(path: string, location: Location = window.location): string {
  const forHost = isStatusHost(location)
    ? path.replace(/^\/status/, "") || "/"
    : path;
  return location.hash.startsWith("#/") ? `#${path}` : forHost;
}
